/**
 * Authentication Utilities
 * Helper functions for auth checks
 */

export const isAuthenticated = (): boolean => {
  return !!localStorage.getItem('authToken');
};

/**
 * Normalize role for consistent comparison
 * Converts to uppercase to match backend format
 */
const normalizeRole = (role: string | null | undefined): string | null => {
  if (!role) return null;
  return String(role).toUpperCase();
};

export const getUserRole = (): string | null => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      return normalizeRole(user.role);
    } catch (e) {
      return null;
    }
  }
  return null;
};

/**
 * Check if user has admin role
 */
export const isAdmin = (): boolean => {
  const userRole = getUserRole();
  return userRole === 'AGENCY_ADMIN' || userRole === 'SUPER_ADMIN' || userRole === 'ADMIN';
};

/**
 * Check if user has doctor role
 */
export const isDoctor = (): boolean => {
  const userRole = getUserRole();
  return userRole === 'DOCTOR';
};

/**
 * Check if user has receptionist role
 */
export const isReceptionist = (): boolean => {
  const userRole = getUserRole();
  return userRole === 'RECEPTIONIST';
};

/**
 * Check if user can access a route based on allowed roles
 */
export const canAccessRoute = (allowedRoles?: string[]): boolean => {
  if (!allowedRoles || allowedRoles.length === 0) {
    return true; // No restrictions
  }
  
  const userRole = getUserRole();
  if (!userRole) {
    return false;
  }
  
  // Normalize allowed roles to uppercase for comparison
  const normalizedAllowedRoles = allowedRoles.map(role => role.toUpperCase());
  return normalizedAllowedRoles.includes(userRole);
};

export const getUser = (): any | null => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch (e) {
      return null;
    }
  }
  return null;
};

/**
 * Get the default dashboard path for the current user based on role
 * All users go directly to their dashboard (onboarding redirects disabled)
 */
export const getDefaultDashboardPath = (): string => {
  const user = getUser();
  if (!user) {
    return '/login';
  }

  const role = user.role?.toUpperCase();
  
  // Home Health Agency - all users go to main dashboard
  if (role === 'AGENCY_ADMIN' || role === 'SUPER_ADMIN' || role === 'ADMIN') {
    return '/dashboard';
  }
  
  // Staff/Caregivers also go to dashboard
  if (role === 'CAREGIVER' || role === 'NURSE' || role === 'STAFF') {
    return '/dashboard';
  }
  
  // Default fallback to main dashboard (referral management)
  return '/dashboard';
};

export const clearAuth = (): void => {
  // Clear all local storage data on logout
  localStorage.clear();
};
